// API functions for template creation and variation generation
import axios from 'axios';
import { reportApiOutage, reportError } from './errorReporter';
import { getAuthToken, getSession } from './authService';
import { isUuidString } from './utils';
import { getRequestOrganizationId } from './workspaceRequestContext';

const API_BASE_URL =
  process.env.REACT_APP_API_HOST?.trim().replace(/\/$/, '') || undefined;

/**
 * Throws if the app was built without an API host (same check pattern as tool routes).
 */
export function requireApiHost(): string {
  if (!API_BASE_URL) {
    throw new Error(
      'API is not configured. Set REACT_APP_API_HOST (same as the dashboard).'
    );
  }
  return API_BASE_URL;
}

// Create axios instance with interceptor for authentication
const apiClient = axios.create({
  baseURL: API_BASE_URL,
});

/**
 * Same base URL as {@link apiClient}, but requests do not require a session.
 * When the user is signed in, a Bearer token is attached so the backend can treat
 * calls consistently. Use for public / optional-auth endpoints.
 */
export const publicApiClient = axios.create({
  baseURL: API_BASE_URL,
});

publicApiClient.interceptors.request.use(
  async config => {
    try {
      const session = await getSession();
      if (session?.access_token) {
        config.headers.Authorization = `Bearer ${session.access_token}`;
      } else {
        delete config.headers.Authorization;
      }
    } catch {
      delete config.headers.Authorization;
    }
    return config;
  },
  error => Promise.reject(error)
);

publicApiClient.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config as
      | (NonNullable<typeof error.config> & { _retry?: boolean })
      | undefined;

    if (
      !originalRequest ||
      error.response?.status !== 401 ||
      originalRequest._retry
    ) {
      reportApiOutage(error);
      return Promise.reject(error);
    }

    const hadAuth = Boolean(originalRequest.headers?.Authorization);
    if (!hadAuth) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    try {
      const { clearAuthTokenCache } = await import('./authService');
      clearAuthTokenCache();
      const token = await getAuthToken();
      originalRequest.headers.Authorization = `Bearer ${token}`;
      return publicApiClient(originalRequest);
    } catch {
      const { clearAuthTokenCache } = await import('./authService');
      clearAuthTokenCache();
      return Promise.reject(error);
    }
  }
);

// Add request interceptor to include auth token
apiClient.interceptors.request.use(
  async config => {
    try {
      const token = await getAuthToken();
      config.headers.Authorization = `Bearer ${token}`;
      const orgId = getRequestOrganizationId();
      if (orgId) {
        config.headers['X-Organization-Id'] = orgId;
      } else {
        delete config.headers['X-Organization-Id'];
      }
    } catch (error) {
      // If we can't get a token, reject the request
      // This will cause the API call to fail, which is correct since
      // all endpoints now require authentication
      return Promise.reject(
        new Error('Authentication required. Please sign in.')
      );
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle 401 errors (expired tokens)
apiClient.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;

    // If we get a 401 and haven't already retried, try to refresh the token
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        // Clear token cache to force a fresh token
        const { clearAuthTokenCache } = await import('./authService');
        clearAuthTokenCache();

        // Get a fresh token (this will trigger Supabase to refresh if possible)
        const token = await getAuthToken();
        originalRequest.headers.Authorization = `Bearer ${token}`;

        // Retry the original request with the new token
        return apiClient(originalRequest);
      } catch (refreshError) {
        // If token refresh fails, the user needs to sign in again
        // Clear the cache and let the error propagate
        const { clearAuthTokenCache } = await import('./authService');
        clearAuthTokenCache();
        return Promise.reject(refreshError);
      }
    }

    reportApiOutage(error);
    return Promise.reject(error);
  }
);

export interface TemplateElement {
  id: string;
  name?: string; // Display name for the element
  originalName?: string; // Original name from backend (layer.name)
  type: 'text' | 'image';
  x: number;
  y: number;
  width: number;
  height: number;
  rotation?: number; // Rotation in degrees
  position?: number; // Z-order position (0=bottom/back, higher=top/front)
  pageNumber?: number; // Page index within a multi-page template (0-based)
  /** @deprecated Use pageNumber — present when template was loaded from raw API JSON */
  page_number?: number;
  // Text-specific properties
  text?: string;
  fontFamily?: string;
  fontSize?: number;
  fontWeight?: string; // Changed to string to match backend
  letterSpacing?: number;
  lineSpacing?: number;
  fontAscentEm?: number;
  fontDescentEm?: number;
  underline?: boolean;
  italic?: boolean;
  color?: string;
  textAlign?: 'left' | 'center' | 'right';
  textDirection?: 'rtl'; // only set for RTL languages; absent = LTR
  verticalAlign?: 'top' | 'middle' | 'bottom';
  textBackgroundColor?: string;
  textBackgroundRoundness?: number;
  textBackgroundSpread?: number;
  // Image-specific properties
  imageUrl?: string;
  imageFit?: 'cover' | 'contain';
  imagePosition?:
    | 'top-left'
    | 'top-center'
    | 'top-right'
    | 'center-left'
    | 'center'
    | 'center-right'
    | 'bottom-left'
    | 'bottom-center'
    | 'bottom-right';
  imageCropX?: number;
  imageCropY?: number;
  imageCropWidth?: number;
  imageCropHeight?: number;
  signedUrlExpiry?: string; // ISO timestamp for when the signed URL expires
  /** True when SVG has multi-color mapping (render as img to show colors; false = use color as mask, including intentional black) */
  hasSvgColorReplacements?: boolean;
  // Common properties
  opacity?: number; // Opacity value (0.0 to 1.0)
  flipHorizontal?: boolean; // Flip the layer horizontally
  flipVertical?: boolean; // Flip the layer vertically
}

export interface Template {
  id: string;
  name: string;
  canvaUrl: string;
  elements: TemplateElement[];
  width?: number;
  height?: number;
  backgroundColor?: string; // Page 0 background color
  pageBackgrounds?: Record<string, string>; // Per-page backgrounds keyed by page index string
  previewUrl?: string; // Preview image URL from backend
  variantCount?: number; // Number of variants (only in list endpoint)
  pageCount?: number; // Number of pages in this template
  warnings?: string[];
  updatedAt?: string; // ISO timestamp string
  tags: string[]; // User-defined tags for organizing designs into folders
}

export interface TagCount {
  name: string;
  count: number;
}

/** Resolve background color for a specific page (0-based). */
export function getPageBackground(
  template: Pick<Template, 'backgroundColor' | 'pageBackgrounds'>,
  pageNumber: number
): string {
  const fallback = template.backgroundColor || 'transparent';
  const pageBackgrounds = template.pageBackgrounds ?? {};
  return pageBackgrounds[String(pageNumber)] ?? fallback;
}

/** Apply a background color update for a specific page (0-based). */
export function setPageBackground(
  template: Pick<Template, 'backgroundColor' | 'pageBackgrounds'>,
  pageNumber: number,
  color: string
): Pick<Template, 'backgroundColor' | 'pageBackgrounds'> {
  const pageBackgrounds = { ...(template.pageBackgrounds ?? {}) };
  pageBackgrounds[String(pageNumber)] = color;
  if (pageNumber === 0) {
    return { backgroundColor: color, pageBackgrounds };
  }
  return { pageBackgrounds };
}

export interface Variation {
  id: string;
  templateId: string;
  elements: TemplateElement[];
  previewUrl: string;
  downloadUrl: string;
  signedUrlExpiry?: string; // ISO timestamp for when the signed URL expires
  warnings?: string[];
  exportType?: 'png' | 'pdf' | 'jpeg' | 'webp'; // Export format type
  isCombinedPdf?: boolean;
  combinedSectionCount?: number;
  createdAt?: string;
}

export function sortVariationsByCreatedAt(
  variations: Variation[]
): Variation[] {
  return [...variations].sort((a, b) => {
    const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    if (aTime !== bTime) {
      return aTime - bTime;
    }
    return a.id.localeCompare(b.id);
  });
}

// Sample Canva URLs for demonstration
export const sampleCanvaUrls = [
  {
    url: 'https://canva.link/pilxlwi49k9nkrs',
    imageName: '/canva_designs/canva_design_1.png',
    label: 'Facebook/Instagram Ad',
  },
  {
    url: 'https://canva.link/0zmehzealtkx1rz',
    imageName: '/canva_designs/canva_design_2.png',
    label: 'Etsy Listing',
  },
  {
    url: 'https://canva.link/qo19actzxf30q4r',
    imageName: '/canva_designs/canva_design_3.png',
    label: 'LinkedIn Banner',
  },
];

// Font weight mapping - same as backend
const FONT_WEIGHT_MAPPING = {
  thin: '100',
  extralight: '200',
  regular: '400',
  normal: '400',
  light: '300',
  medium: '500',
  semibold: '600',
  bold: '700',
  extrabold: '800',
  heavy: '800',
  ultrabold: '900',
  black: '900',
};

// Helper function to convert font weight (string or number from API) to CSS numeric value
export const convertFontWeightToNumber = (weight?: string | number): number => {
  if (weight === undefined || weight === null) return 400;

  // Already a number (e.g. from API JSON)
  if (typeof weight === 'number') {
    const n = Math.round(weight);
    return n >= 100 && n <= 900 ? n : 400;
  }

  // Try to parse as number first
  const numWeight = parseInt(weight, 10);
  if (!isNaN(numWeight)) return numWeight;

  // Otherwise look up in mapping
  const mappedWeight =
    FONT_WEIGHT_MAPPING[
      weight.toLowerCase() as keyof typeof FONT_WEIGHT_MAPPING
    ];
  return mappedWeight ? parseInt(mappedWeight, 10) : 400;
};

/** Backend may set svg_color_replacements to true (flag) or a color map (object). */
function layerHasSvgColorReplacements(properties: unknown): boolean {
  if (!properties || typeof properties !== 'object') return false;
  const raw = (properties as Record<string, unknown>).svg_color_replacements;
  if (raw === true) return true;
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    return Object.keys(raw as Record<string, unknown>).length > 0;
  }
  return false;
}

/**
 * Use CSS mask+tint only for small single-glyph style SVGs. Large on-canvas SVGs
 * (illustrations) often carry a Canva "display" color / partial color map while the
 * asset is full-color — masking would flatten them to one fill (e.g. #9f96ca).
 */
export function shouldUseSvgColorMask(element: TemplateElement): boolean {
  if (element.type !== 'image') return false;
  const url = element.imageUrl || '';
  const isSvg =
    url.toLowerCase().includes('.svg') || url.startsWith('data:image/svg+xml');
  if (!isSvg) return false;
  if (!element.color || typeof element.color !== 'string') return false;
  if (element.color.trim().toLowerCase() === '#000000') return false;
  if (element.hasSvgColorReplacements) return false;
  const area = (element.width || 0) * (element.height || 0);
  if (area >= 50_000) return false;
  return true;
}

/** 0-based page index for a layer (handles camelCase and snake_case). */
export function getElementPageNumber(element: TemplateElement): number {
  const raw = element.pageNumber ?? element.page_number ?? 0;
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

// Transform backend layer data to frontend TemplateElement format
function transformLayerToElement(layer: any): TemplateElement {
  const baseElement = {
    id: layer.id,
    name: layer.name,
    originalName: layer.name,
    type: layer.layer_type as 'text' | 'image',
    x: layer.x,
    y: layer.y,
    width: layer.properties.width || 0,
    height: layer.properties.height || 0,
    rotation: layer.properties.rotation || 0,
    position: layer.position || 0,
    pageNumber: getElementPageNumber(layer as TemplateElement),
    color: layer.properties.color,
    opacity:
      layer.properties.opacity !== undefined ? layer.properties.opacity : 1.0,
    flipHorizontal: layer.properties.flip_horizontal || false,
    flipVertical: layer.properties.flip_vertical || false,
  };

  // Add text-specific properties
  if (layer.layer_type === 'text') {
    return {
      ...baseElement,
      text: layer.properties.text || '',
      fontFamily: layer.properties.font_name,
      fontSize: layer.properties.font_size || 16,
      fontWeight: layer.properties.font_style?.weight || 'regular',
      italic: layer.properties.font_style?.italic || false,
      underline: layer.properties.font_style?.underline || false,
      textAlign: layer.properties.text_align || 'left',
      textDirection:
        layer.properties.text_direction === 'rtl' ? 'rtl' : undefined,
      verticalAlign: layer.properties.vertical_align,
      letterSpacing: layer.properties.letter_spacing || 0,
      lineSpacing: layer.properties.line_spacing || 1400,
      fontAscentEm: layer.properties.font_ascent_em,
      fontDescentEm: layer.properties.font_descent_em,
      textBackgroundColor: layer.properties.text_background_color,
      textBackgroundRoundness: layer.properties.text_background_roundness,
      textBackgroundSpread: layer.properties.text_background_spread,
    } as TemplateElement;
  }

  // Add image-specific properties
  if (layer.layer_type === 'image') {
    return {
      ...baseElement,
      imageUrl: layer.properties.img_url,
      imageFit: layer.properties.image_fit || 'cover',
      imagePosition: layer.properties.image_position || 'center',
      imageCropX: layer.properties.image_crop_x,
      imageCropY: layer.properties.image_crop_y,
      imageCropWidth: layer.properties.image_crop_width,
      imageCropHeight: layer.properties.image_crop_height,
      signedUrlExpiry: layer.properties.signed_url_expiry || undefined,
      hasSvgColorReplacements: layerHasSvgColorReplacements(layer.properties),
    } as TemplateElement;
  }

  return baseElement as TemplateElement;
}

function mapTemplateFromApi(data: any): Template {
  const layers = Array.isArray(data?.layers) ? data.layers : [];
  return {
    id: data.id,
    name: data.name,
    canvaUrl: data.source_url,
    width: data.width,
    height: data.height,
    backgroundColor: data.background_color,
    pageBackgrounds: data.page_backgrounds ?? {},
    previewUrl: data.preview_url,
    elements: layers.map(transformLayerToElement),
    pageCount: data.page_count ?? 1,
    warnings: data.warnings || [],
    tags: data.tags || [],
    ...(data.updated_at !== undefined ? { updatedAt: data.updated_at } : {}),
    ...(data.variant_count !== undefined
      ? { variantCount: data.variant_count }
      : {}),
  };
}

/** Check if an error is transient and worth retrying (network, timeout, 5xx). */
function isRetryableError(error: unknown): boolean {
  if (!axios.isAxiosError(error)) return false;
  // No response = network error (connection refused, DNS, CORS, etc.)
  if (!error.response) return true;
  const status = error.response.status;
  // Retry on 5xx server errors
  if (status >= 500 && status < 600) return true;
  // Retry on 408 Request Timeout, 429 Too Many Requests
  if (status === 408 || status === 429) return true;
  return false;
}

const READ_REQUEST_MAX_RETRIES = 3;
const READ_REQUEST_INITIAL_DELAY_MS = 700;

async function requestWithRetry<T>(
  requestFn: () => Promise<T>,
  maxRetries: number = READ_REQUEST_MAX_RETRIES,
  initialDelayMs: number = READ_REQUEST_INITIAL_DELAY_MS,
  onRetrying?: (nextAttempt: number, maxAttempts: number) => void
): Promise<T> {
  let lastError: unknown;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await requestFn();
    } catch (error) {
      lastError = error;
      const canRetry = attempt < maxRetries - 1 && isRetryableError(error);
      if (!canRetry) {
        throw error;
      }

      onRetrying?.(attempt + 2, maxRetries);
      const delayMs = initialDelayMs * Math.pow(2, attempt);
      await new Promise(resolve => setTimeout(resolve, delayMs));
    }
  }

  throw lastError;
}

const TEMPLATE_CREATE_MAX_RETRIES = 3;
const TEMPLATE_CREATE_INITIAL_DELAY_MS = 2000;
const TEMPLATE_CREATE_TIMEOUT_MS = 120_000; // 2 min - Playwright + Canva fetch can be slow

export interface CreateTemplateOptions {
  /** Called when a retry is about to start (e.g. after timeout or network error). */
  onRetrying?: (attempt: number, maxAttempts: number) => void;
  /** 0-based Canva page indexes to import. Omit to import the plan's default prefix. */
  pageNumbers?: number[];
}

// Create template from Canva URL via backend API (with retries for transient network errors)
export async function createTemplate(
  canvaUrl: string,
  options?: CreateTemplateOptions
): Promise<Template> {
  const { onRetrying, pageNumbers } = options ?? {};
  try {
    const response = await requestWithRetry(
      () =>
        apiClient.post(
          `/v1/template`,
          {
            canva_url: canvaUrl,
            ...(pageNumbers && pageNumbers.length > 0
              ? { page_numbers: pageNumbers }
              : {}),
          },
          {
            timeout: TEMPLATE_CREATE_TIMEOUT_MS,
          }
        ),
      TEMPLATE_CREATE_MAX_RETRIES,
      TEMPLATE_CREATE_INITIAL_DELAY_MS,
      onRetrying
    );

    return mapTemplateFromApi(response.data);
  } catch (error) {
    // Report frontend-specific errors (backend errors already reported)
    await reportError({
      context: 'Creating template from Canva URL',
      error,
      additionalInfo: { canvaUrl },
    });

    if (axios.isAxiosError(error)) {
      const message = error.response?.data?.detail || error.message;
      const status = error.response?.status;
      const customError = new Error(
        `Failed to create template: ${message}`
      ) as Error & { status?: number };
      customError.status = status;
      throw customError;
    }
    throw error;
  }
}

export interface CanvaImportPreview {
  title: string;
  width: number;
  height: number;
  /** Total pages in the Canva design */
  pageCount: number;
  /** Pages that will be imported on the user's current plan */
  importablePageCount: number;
  /** Max pages the user's plan can import */
  maxPages: number;
  /** One screenshot URL per page, in order (may be empty) */
  pageThumbnails: string[];
  /** Same URL repeated; each tile shows one slice of a vertical Canva strip */
  pageThumbnailsStacked?: boolean;
  /** Licensed graphics that will import with a watermark */
  watermarkedImages: string[];
  /** Font families that will render as Inter */
  fontInterFallbacks: string[];
}

const IMPORT_PREVIEW_TIMEOUT_MS = 90_000;

/**
 * Fetch a lightweight summary (page screenshots, page count, plan page limit)
 * of a Canva design without creating a template. Errors carry `status` so the
 * caller can tell user-fixable problems (4xx) from transient ones.
 */
export async function previewCanvaImport(
  canvaUrl: string
): Promise<CanvaImportPreview> {
  try {
    const response = await apiClient.post(
      `/v1/template/import-preview`,
      { canva_url: canvaUrl },
      { timeout: IMPORT_PREVIEW_TIMEOUT_MS }
    );
    const data = response.data;
    return {
      title: data.title,
      width: data.width,
      height: data.height,
      pageCount: data.page_count,
      importablePageCount: data.importable_page_count,
      maxPages: data.max_pages,
      pageThumbnails: data.page_thumbnails ?? [],
      pageThumbnailsStacked: data.page_thumbnails_stacked === true,
      watermarkedImages: data.watermarked_images ?? [],
      fontInterFallbacks: data.font_inter_fallbacks ?? [],
    };
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = error.response?.data?.detail || error.message;
      const customError = new Error(message) as Error & { status?: number };
      customError.status = error.response?.status;
      throw customError;
    }
    throw error;
  }
}

// Get template by ID (with refreshed signed URLs)
export async function getTemplate(templateId: string): Promise<Template> {
  try {
    const response = await apiClient.get(`/v1/template/${templateId}`);

    return mapTemplateFromApi(response.data);
  } catch (error) {
    // Report frontend-specific errors (backend errors already reported)
    await reportError({
      context: 'Fetching template by ID',
      error,
      additionalInfo: { templateId },
    });

    if (axios.isAxiosError(error)) {
      const message = error.response?.data?.detail || error.message;
      throw new Error(`Failed to fetch template: ${message}`);
    }
    throw error;
  }
}

// Helper function to convert frontend TemplateElement to backend layer format
// Sends complete layer data since we're updating entire modified layers
function transformElementToLayerUpdate(element: TemplateElement) {
  const baseUpdate: any = {
    name: element.name,
    x: element.x,
    y: element.y,
    position: element.position !== undefined ? element.position : 0,
    page_number: element.pageNumber ?? 0,
    properties: {
      width: element.width,
      height: element.height,
      rotation: element.rotation || 0,
    } as any,
  };

  if (element.color) {
    baseUpdate.properties.color = element.color;
  }

  if (element.opacity !== undefined) {
    baseUpdate.properties.opacity = element.opacity;
  }

  if (element.flipHorizontal !== undefined) {
    baseUpdate.properties.flip_horizontal = element.flipHorizontal;
  }

  if (element.flipVertical !== undefined) {
    baseUpdate.properties.flip_vertical = element.flipVertical;
  }

  // Type-specific properties
  if (element.type === 'text') {
    baseUpdate.properties.text = element.text || '';
    baseUpdate.properties.font_size = element.fontSize || 16;
    baseUpdate.properties.font_name = element.fontFamily;
    baseUpdate.properties.text_align = element.textAlign;
    if (element.textDirection === 'rtl') {
      baseUpdate.properties.text_direction = 'rtl';
    }
    baseUpdate.properties.vertical_align = element.verticalAlign;
    baseUpdate.properties.letter_spacing = element.letterSpacing || 0;
    baseUpdate.properties.line_spacing = element.lineSpacing || 1400;
    if (element.fontAscentEm != null) {
      baseUpdate.properties.font_ascent_em = element.fontAscentEm;
    }
    if (element.fontDescentEm != null) {
      baseUpdate.properties.font_descent_em = element.fontDescentEm;
    }
    baseUpdate.properties.font_style = {
      weight: element.fontWeight || 'regular',
      italic: element.italic || false,
      underline: element.underline || false,
    };
    if (element.textBackgroundColor) {
      baseUpdate.properties.text_background_color = element.textBackgroundColor;
    }
    if (element.textBackgroundRoundness != null) {
      baseUpdate.properties.text_background_roundness =
        element.textBackgroundRoundness;
    }
    if (element.textBackgroundSpread != null) {
      baseUpdate.properties.text_background_spread =
        element.textBackgroundSpread;
    }
  } else if (element.type === 'image') {
    baseUpdate.properties.img_url = element.imageUrl || '';
    baseUpdate.properties.image_fit = element.imageFit || 'cover';
    if (element.imageCropWidth && element.imageCropHeight) {
      baseUpdate.properties.image_crop_x = element.imageCropX ?? 0;
      baseUpdate.properties.image_crop_y = element.imageCropY ?? 0;
      baseUpdate.properties.image_crop_width = element.imageCropWidth;
      baseUpdate.properties.image_crop_height = element.imageCropHeight;
    }
  }

  return baseUpdate;
}

export async function updateTemplate(
  template: Template,
  includeTemplateProperties: boolean = false,
  options?: { silent?: boolean }
): Promise<null> {
  try {
    const payload: any = {};

    // Include template-level properties if they were modified
    if (includeTemplateProperties) {
      payload.name = template.name;
      payload.width = template.width;
      payload.height = template.height;
      payload.background_color = template.backgroundColor;
      if (
        template.pageBackgrounds &&
        Object.keys(template.pageBackgrounds).length > 0
      ) {
        payload.page_backgrounds = template.pageBackgrounds;
      }
    }

    // Include layers (only modified ones are passed in template.elements)
    if (template.elements && template.elements.length > 0) {
      payload.layers = template.elements.map(element => ({
        id: element.id,
        ...transformElementToLayerUpdate(element),
      }));
    }
    if (Object.keys(payload).length > 0) {
      await requestWithRetry(() =>
        apiClient.patch(`/v1/template/${template.id}`, payload)
      );
    }

    return null;
  } catch (error) {
    if (!options?.silent) {
      await reportError({
        context: 'Updating template',
        error,
        additionalInfo: { templateId: template.id },
      });
    }

    if (axios.isAxiosError(error)) {
      const message = error.response?.data?.detail || error.message;
      throw new Error(`Failed to update template: ${message}`);
    }
    throw error;
  }
}

// Create a new layer
export async function createLayer(
  templateId: string,
  element: TemplateElement
): Promise<TemplateElement> {
  try {
    const layerData = {
      name: element.name || 'New Layer',
      layer_type: element.type,
      x: element.x,
      y: element.y,
      properties: {} as any,
    };

    // Add type-specific properties
    if (element.type === 'text') {
      layerData.properties = {
        text: element.text || '',
        font_size: element.fontSize || 16,
        font_name: element.fontFamily || 'Arial',
        font_style: {
          weight: element.fontWeight || 'regular',
          italic: element.italic || false,
          underline: element.underline || false,
        },
        color: element.color || '#000000',
        letter_spacing: element.letterSpacing || 0,
        text_align: element.textAlign || 'left',
        vertical_align: element.verticalAlign || 'top',
        width: element.width,
        height: element.height,
        rotation: element.rotation || 0,
        ...(element.fontAscentEm != null
          ? { font_ascent_em: element.fontAscentEm }
          : {}),
        ...(element.fontDescentEm != null
          ? { font_descent_em: element.fontDescentEm }
          : {}),
      };
    } else if (element.type === 'image') {
      layerData.properties = {
        img_url: element.imageUrl || '',
        image_fit: element.imageFit || 'cover',
        width: element.width,
        height: element.height,
        rotation: element.rotation || 0,
      };
    }

    const response = await apiClient.post(
      `/v1/template/${templateId}/layer`,
      layerData
    );

    // Transform the response back to TemplateElement format
    const createdLayer = response.data;
    return transformLayerToElement(createdLayer);
  } catch (error) {
    await reportError({
      context: 'Creating layer',
      error,
      additionalInfo: { templateId, elementType: element.type },
    });

    if (axios.isAxiosError(error)) {
      const message = error.response?.data?.detail || error.message;
      throw new Error(`Failed to create layer: ${message}`);
    }
    throw error;
  }
}

/** Turn typed "\\n" / "\\r\\n" escape sequences into real line breaks for text overrides. */
export function normalizeTextOverrideNewlines(value: string): string {
  return value
    .replace(/\\r\\n/g, '\n')
    .replace(/\\n/g, '\n')
    .replace(/\\r/g, '\n');
}

// Coerce string value to proper type for API
function coerceOverrideValue(
  property: string,
  value: string
): string | number | boolean | null {
  const numericProps = [
    'fontSize',
    'letterSpacing',
    'lineSpacing',
    'opacity',
    'width',
    'height',
    'rotation',
  ];
  const booleanProps = [
    'italic',
    'underline',
    'flipHorizontal',
    'flipVertical',
  ];
  if (property === 'textDirection') {
    // Only send 'rtl' when enabled; omit when off so base layer LTR/RTL is preserved
    return value === 'true' ? 'rtl' : null;
  }
  if (property === 'text') {
    return normalizeTextOverrideNewlines(value);
  }
  if (numericProps.includes(property)) {
    const num = parseFloat(value);
    return isNaN(num) ? value : num;
  }
  if (booleanProps.includes(property)) {
    return value === 'true';
  }
  return value;
}

export function detectExportTypeFromUrl(
  url: string | undefined,
  fallback: 'png' | 'pdf' | 'jpeg' | 'webp' = 'png'
): 'png' | 'pdf' | 'jpeg' | 'webp' {
  if (!url) return fallback;
  let path = url;
  try {
    path = new URL(url).pathname.toLowerCase();
  } catch {
    path = url.split('?')[0].toLowerCase();
  }
  if (path.endsWith('.pdf')) return 'pdf';
  if (path.endsWith('.jpeg') || path.endsWith('.jpg')) return 'jpeg';
  if (path.endsWith('.webp')) return 'webp';
  return fallback;
}

const OVERRIDE_PROPERTY_MAPPING: Record<string, string> = {
  text: 'text',
  imageUrl: 'img_url',
  imageFit: 'image_fit',
  imagePosition: 'image_position',
  fontFamily: 'font_name',
  fontWeight: 'font_style.weight',
  fontSize: 'font_size',
  textAlign: 'text_align',
  textDirection: 'text_direction',
  verticalAlign: 'vertical_align',
  letterSpacing: 'letter_spacing',
  lineSpacing: 'line_spacing',
  color: 'color',
  italic: 'font_style.italic',
  underline: 'font_style.underline',
  opacity: 'opacity',
  flipHorizontal: 'flip_horizontal',
  flipVertical: 'flip_vertical',
  width: 'width',
  height: 'height',
  rotation: 'rotation',
};

function buildOverridesPayload(
  overrideArray: Array<{ layerId: string; property: string; value: string }>
): Array<{ layer_id: string; properties: Record<string, unknown> }> {
  const overrideItemsWithProps = overrideArray.flatMap(overrideItem => {
    const properties: Record<string, unknown> = {};
    const backendProperty =
      OVERRIDE_PROPERTY_MAPPING[overrideItem.property] || overrideItem.property;
    const coercedValue = coerceOverrideValue(
      overrideItem.property,
      overrideItem.value
    );

    if (coercedValue === null) {
      return [];
    }

    if (backendProperty.includes('.')) {
      const [parent, child] = backendProperty.split('.');
      properties[parent] = { [child]: coercedValue };
    } else {
      properties[backendProperty] = coercedValue;
    }

    return [
      {
        layer_id: overrideItem.layerId,
        properties,
      },
    ];
  });

  const mergedByLayer = new Map<
    string,
    { layer_id: string; properties: Record<string, unknown> }
  >();
  for (const item of overrideItemsWithProps) {
    const layerId = item.layer_id;
    const existing = mergedByLayer.get(layerId);

    if (existing) {
      const mergedProps = { ...existing.properties };
      for (const [key, val] of Object.entries(item.properties)) {
        if (key === 'font_style' && val && typeof val === 'object') {
          mergedProps.font_style = {
            ...(mergedProps.font_style as Record<string, unknown>),
            ...(val as Record<string, unknown>),
          };
        } else {
          mergedProps[key] = val;
        }
      }
      mergedByLayer.set(layerId, {
        layer_id: layerId,
        properties: mergedProps,
      });
    } else {
      mergedByLayer.set(layerId, item);
    }
  }

  return Array.from(mergedByLayer.values());
}

function transformVariantResponse(
  data: {
    id: string;
    template_id: string;
    url: string;
    signed_url_expiry?: string;
    warnings?: string[];
    is_combined_pdf?: boolean;
    combined_section_count?: number;
    created_at?: string;
  },
  exportType: 'png' | 'pdf' | 'jpeg' | 'webp'
): Variation {
  const warnings = data.warnings || [];
  const finalExportType = exportType || detectExportTypeFromUrl(data.url);
  return {
    id: data.id,
    templateId: data.template_id,
    elements: [],
    previewUrl: data.url,
    downloadUrl: data.url,
    signedUrlExpiry: data.signed_url_expiry || undefined,
    warnings,
    exportType: finalExportType,
    isCombinedPdf: data.is_combined_pdf || false,
    combinedSectionCount: data.combined_section_count,
    createdAt: data.created_at,
  };
}

const MAX_BULK_VARIANTS = 50;

export interface BulkVariationResult {
  index: number;
  success: boolean;
  variation?: Variation;
  error?: string;
}

export interface CombinedPdfResult {
  url: string;
  signedUrlExpiry?: string;
  variation?: Variation;
}

export interface BulkVariationResponse {
  results: BulkVariationResult[];
  totalRequested: number;
  totalCreated: number;
  totalFailed: number;
  combinedPdf?: CombinedPdfResult;
}

// Create variant via backend API
export async function createVariation(
  templateId: string,
  override:
    | { layerId: string; property: string; value: string }
    | { layerId: string; property: string; value: string }[],
  exportType: 'png' | 'pdf' | 'jpeg' | 'webp' = 'png',
  pageNumber: number = 0
): Promise<{ variation: Variation; warnings: string[] }> {
  // Normalize single override to array
  const overrideArray = Array.isArray(override) ? override : [override];

  try {
    const overrides = buildOverridesPayload(overrideArray);

    const response = await apiClient.post(
      `/v1/template/${templateId}/variant`,
      {
        overrides: overrides,
        export_type: exportType,
        page_number: pageNumber,
      }
    );

    const data = response.data;
    const variation = transformVariantResponse(data, exportType);
    const warnings = data.warnings || [];

    return { variation, warnings };
  } catch (error) {
    // Report frontend-specific errors (backend errors already reported)
    await reportError({
      context: 'Creating variation',
      error,
      additionalInfo: { templateId, numOverrides: overrideArray?.length || 0 },
    });

    if (axios.isAxiosError(error)) {
      // Preserve the original axios error for rate limit detection
      // Don't wrap 429 errors - let the component handle them
      if (error.response?.status === 429) {
        throw error; // Throw the original axios error
      }
      const message = error.response?.data?.detail || error.message;
      throw new Error(`Failed to create variation: ${message}`);
    }
    throw error;
  }
}

export async function createVariationsBulk(
  templateId: string,
  exportType: 'png' | 'pdf' | 'jpeg' | 'webp',
  items: Array<{
    overrides: Array<{ layerId: string; property: string; value: string }>;
    pageNumber?: number;
  }>,
  combinePdf = false
): Promise<BulkVariationResponse> {
  const aggregated: BulkVariationResult[] = [];
  let totalRequested = 0;
  let totalCreated = 0;
  let totalFailed = 0;
  let combinedPdf: CombinedPdfResult | undefined;

  for (let offset = 0; offset < items.length; offset += MAX_BULK_VARIANTS) {
    const chunk = items.slice(offset, offset + MAX_BULK_VARIANTS);
    const chunkOffset = offset;

    try {
      const response = await apiClient.post(
        `/v1/template/${templateId}/variant/bulk`,
        {
          export_type: exportType,
          combine_pdf: combinePdf,
          items: chunk.map(item => ({
            overrides: buildOverridesPayload(item.overrides),
            page_number: item.pageNumber ?? 0,
          })),
        }
      );

      const data = response.data;
      totalRequested += data.total_requested ?? chunk.length;
      totalCreated += data.total_created ?? 0;
      totalFailed += data.total_failed ?? 0;

      for (const result of data.results ?? []) {
        const mapped: BulkVariationResult = {
          index: chunkOffset + result.index,
          success: result.success,
          error: result.error,
        };
        if (result.variant) {
          mapped.variation = transformVariantResponse(
            result.variant,
            exportType
          );
        }
        aggregated.push(mapped);
      }

      if (data.combined_pdf?.url) {
        combinedPdf = {
          url: data.combined_pdf.url,
          signedUrlExpiry: data.combined_pdf.signed_url_expiry,
          variation: data.combined_pdf.variant
            ? transformVariantResponse(data.combined_pdf.variant, exportType)
            : undefined,
        };
      }
    } catch (error) {
      await reportError({
        context: 'Creating variations in bulk',
        error,
        additionalInfo: {
          templateId,
          chunkSize: chunk.length,
          combinePdf,
        },
      });

      if (axios.isAxiosError(error) && error.response?.status === 429) {
        throw error;
      }

      if (axios.isAxiosError(error)) {
        const message = error.response?.data?.detail || error.message;
        throw new Error(`Failed to create variations in bulk: ${message}`);
      }
      throw error;
    }
  }

  aggregated.sort((a, b) => a.index - b.index);

  return {
    results: aggregated,
    totalRequested,
    totalCreated,
    totalFailed,
    combinedPdf,
  };
}

// Get variations for a template (with auto-refreshed signed URLs)
export async function getVariations(templateId: string): Promise<Variation[]> {
  try {
    const response = await apiClient.get(`/v1/template/${templateId}/variants`);

    const data = response.data;

    // Transform backend response to frontend format
    const variations: Variation[] = data.map((item: any) => {
      const detectedExportType = detectExportTypeFromUrl(item.url);
      return {
        id: item.id,
        templateId: item.template_id,
        elements: [], // We don't need elements for variations
        warnings: item.warnings || [],
        previewUrl: item.url,
        downloadUrl: item.url,
        signedUrlExpiry: item.signed_url_expiry || undefined,
        exportType: detectedExportType,
        isCombinedPdf: item.is_combined_pdf || false,
        combinedSectionCount: item.combined_section_count,
        createdAt: item.created_at,
      };
    });

    return sortVariationsByCreatedAt(variations);
  } catch (error) {
    // Report frontend-specific errors (backend errors already reported)
    await reportError({
      context: 'Fetching variations for template',
      error,
      additionalInfo: { templateId },
    });

    if (axios.isAxiosError(error)) {
      const message = error.response?.data?.detail || error.message;
      throw new Error(`Failed to fetch variations: ${message}`);
    }
    throw error;
  }
}

// List all templates for the current user, optionally filtered by tag and search
export interface ListTemplatesResult {
  templates: Template[];
  hasMore: boolean;
  total: number;
}

export async function listTemplates(
  skip: number = 0,
  limit: number = 100,
  tag?: string,
  q?: string
): Promise<ListTemplatesResult> {
  try {
    const params: Record<string, any> = { skip, limit };
    if (tag) params.tag = tag;
    if (q && q.trim()) params.q = q.trim();

    const response = await requestWithRetry(() =>
      apiClient.get('/v1/templates', { params })
    );

    const data = response.data;
    const hasMoreHeader = response.headers?.['x-has-more'];
    const totalHeader = response.headers?.['x-total-count'];
    const hasMore =
      hasMoreHeader !== undefined
        ? String(hasMoreHeader).toLowerCase() === 'true'
        : Array.isArray(data) && data.length === limit;
    const total =
      totalHeader !== undefined && totalHeader !== ''
        ? Number(totalHeader)
        : Array.isArray(data)
          ? data.length
          : 0;

    const templates: Template[] = (Array.isArray(data) ? data : []).map(
      (item: any) => mapTemplateFromApi(item)
    );

    return {
      templates,
      hasMore,
      total: Number.isFinite(total) ? total : templates.length,
    };
  } catch (error) {
    await reportError({
      context: 'Listing templates',
      error,
    });

    if (axios.isAxiosError(error)) {
      const message = error.response?.data?.detail || error.message;
      throw new Error(`Failed to list templates: ${message}`);
    }
    throw error;
  }
}

// List all unique tags for the current user's templates with their counts
export async function listTemplateTags(): Promise<TagCount[]> {
  try {
    const response = await requestWithRetry(() =>
      apiClient.get('/v1/templates/tags')
    );
    return response.data as TagCount[];
  } catch (error) {
    await reportError({ context: 'Listing template tags', error });
    if (axios.isAxiosError(error)) {
      const message = error.response?.data?.detail || error.message;
      throw new Error(`Failed to list template tags: ${message}`);
    }
    throw error;
  }
}

// Update the tags on a template (replace semantics; [] clears all tags)
export async function updateTemplateTags(
  templateId: string,
  tags: string[]
): Promise<void> {
  try {
    await apiClient.patch(`/v1/template/${templateId}`, { tags });
  } catch (error) {
    await reportError({
      context: 'Updating template tags',
      error,
      additionalInfo: { templateId, tags },
    });
    if (axios.isAxiosError(error)) {
      const message = error.response?.data?.detail || error.message;
      throw new Error(`Failed to update template tags: ${message}`);
    }
    throw error;
  }
}

// Delete a template
export async function deleteTemplate(templateId: string): Promise<void> {
  try {
    await apiClient.delete(`/v1/template/${templateId}`);
  } catch (error) {
    await reportError({
      context: 'Deleting template',
      error,
      additionalInfo: { templateId },
    });

    if (axios.isAxiosError(error)) {
      const message = error.response?.data?.detail || error.message;
      throw new Error(`Failed to delete template: ${message}`);
    }
    throw error;
  }
}

// Rename a template
export async function renameTemplate(
  templateId: string,
  newName: string
): Promise<void> {
  try {
    await apiClient.patch(`/v1/template/${templateId}`, {
      name: newName,
    });
  } catch (error) {
    await reportError({
      context: 'Renaming template',
      error,
      additionalInfo: { templateId, newName },
    });

    if (axios.isAxiosError(error)) {
      const message = error.response?.data?.detail || error.message;
      throw new Error(`Failed to rename template: ${message}`);
    }
    throw error;
  }
}

// Delete a layer
export async function deleteLayer(
  templateId: string,
  layerId: string
): Promise<void> {
  // Pasted / client-only layers use ids like `image_<timestamp>_<random>` until
  // createLayer runs; they never exist on the server, so DELETE must be skipped.
  if (!isUuidString(layerId)) {
    return;
  }

  try {
    await requestWithRetry(() =>
      apiClient.delete(`/v1/template/${templateId}/layer/${layerId}`)
    );
  } catch (error) {
    // Idempotent delete: layer may already be gone if a prior request succeeded without a response
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return;
    }

    await reportError({
      context: 'Deleting layer',
      error,
      additionalInfo: { templateId, layerId },
    });

    if (axios.isAxiosError(error)) {
      const detail = error.response?.data?.detail;
      const message =
        typeof detail === 'string'
          ? detail
          : detail !== undefined
            ? JSON.stringify(detail)
            : error.message;
      throw new Error(`Failed to delete layer: ${message}`);
    }
    throw error;
  }
}

// Delete a variation
export async function deleteVariation(
  templateId: string,
  variationId: string
): Promise<void> {
  try {
    await apiClient.delete(`/v1/template/${templateId}/variant/${variationId}`);
  } catch (error) {
    await reportError({
      context: 'Deleting variation',
      error,
      additionalInfo: { templateId, variationId },
    });

    if (axios.isAxiosError(error)) {
      const message = error.response?.data?.detail || error.message;
      throw new Error(`Failed to delete variation: ${message}`);
    }
    throw error;
  }
}

// Get current user profile
export interface UserProfile {
  id: string;
  email: string | null;
  role: string;
}

export async function getCurrentUserProfile(): Promise<UserProfile> {
  try {
    const response = await requestWithRetry(() =>
      apiClient.get('/v1/users/me')
    );
    return response.data;
  } catch (error) {
    await reportError({
      context: 'Fetching current user profile',
      error,
    });

    if (axios.isAxiosError(error)) {
      const message = error.response?.data?.detail || error.message;
      throw new Error(`Failed to fetch user profile: ${message}`);
    }
    throw error;
  }
}

// ============================================================================
// API Key Management
// ============================================================================

export interface ApiKey {
  id: string;
  name: string;
  key_prefix: string;
  created_at: string;
  last_used_at: string | null;
  is_active: boolean;
}

export interface ApiKeyCreateResponse {
  id: string;
  name: string;
  key: string; // Full key - only returned once!
  key_prefix: string;
  created_at: string;
}

/**
 * Create a new API key
 */
export async function createApiKey(
  name: string
): Promise<ApiKeyCreateResponse> {
  try {
    const response = await apiClient.post('/v1/api-keys/', {
      name,
    });
    return response.data;
  } catch (error) {
    await reportError({
      context: 'Creating API key',
      error,
      additionalInfo: { name },
    });

    if (axios.isAxiosError(error)) {
      const message = error.response?.data?.detail || error.message;
      throw new Error(`Failed to create API key: ${message}`);
    }
    throw error;
  }
}

// Shared promise for concurrent listApiKeys requests to prevent duplicate API calls
let apiKeysPromise: Promise<ApiKey[]> | null = null;

/**
 * List all API keys for the current user
 * Note: Full keys are never returned after creation
 * Deduplicates concurrent requests - if a request is already in flight, returns the same promise
 */
export async function listApiKeys(): Promise<ApiKey[]> {
  // If a request is already in flight, return the existing promise
  if (apiKeysPromise) {
    return apiKeysPromise;
  }

  // Create new promise and store it
  apiKeysPromise = (async () => {
    try {
      const response = await requestWithRetry(() =>
        apiClient.get('/v1/api-keys/')
      );
      return response.data;
    } catch (error) {
      await reportError({
        context: 'Listing API keys',
        error,
      });

      if (axios.isAxiosError(error)) {
        const message = error.response?.data?.detail || error.message;
        throw new Error(`Failed to list API keys: ${message}`);
      }
      throw error;
    } finally {
      // Clear the promise when done (success or error) so future calls can make new requests
      apiKeysPromise = null;
    }
  })();

  return apiKeysPromise;
}

/**
 * Delete an API key
 */
export async function deleteApiKey(keyId: string): Promise<void> {
  try {
    await apiClient.delete(`/v1/api-keys/${keyId}`);
  } catch (error) {
    await reportError({
      context: 'Deleting API key',
      error,
      additionalInfo: { keyId },
    });

    if (axios.isAxiosError(error)) {
      const message = error.response?.data?.detail || error.message;
      throw new Error(`Failed to delete API key: ${message}`);
    }
    throw error;
  }
}

// ============================================================================
// Billing Management
// ============================================================================

export interface BillingInfo {
  has_subscription: boolean;
  subscription_status: string | null;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  stripe_subscription_item_id: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  plan_name: string | null;
  is_early_adopter?: boolean;
  credits_limit?: number;
  can_manage_billing?: boolean;
  is_organization_billing?: boolean;
  usage: {
    cost_units: number;
    total_calls: number;
    start_date: string | null;
    end_date: string | null;
  };
}

/** Clears in-flight dedupe for {@link getBillingInfo} (e.g. after workspace change). */
export function invalidateBillingInfoCache(): void {
  billingInfoPromise = null;
}

/**
 * Initialize billing for a new user.
 * Creates Stripe customer and mapping with free plan period.
 * Idempotent - safe to call multiple times.
 */
export async function initializeBilling(): Promise<{
  status: string;
  message: string;
}> {
  try {
    const response = await requestWithRetry(() =>
      apiClient.post('/v1/billing/initialize')
    );
    return response.data;
  } catch (error) {
    await reportError({
      context: 'Initializing billing',
      error,
    });

    // Don't throw - billing init failure shouldn't block signup
    if (axios.isAxiosError(error)) {
      const message = error.response?.data?.detail || error.message;
      return { status: 'error', message };
    }
    return { status: 'error', message: 'Unknown error' };
  }
}

// Shared promise for concurrent getBillingInfo requests to prevent duplicate API calls
let billingInfoPromise: Promise<BillingInfo> | null = null;

/**
 * Get billing information for the current user
 * Deduplicates concurrent requests - if a request is already in flight, returns the same promise
 */
export async function getBillingInfo(): Promise<BillingInfo> {
  // If a request is already in flight, return the existing promise
  if (billingInfoPromise) {
    return billingInfoPromise;
  }

  // Create new promise and store it
  billingInfoPromise = (async () => {
    try {
      const response = await requestWithRetry(() =>
        apiClient.get('/v1/billing')
      );
      return response.data;
    } catch (error) {
      await reportError({
        context: 'Fetching billing info',
        error,
      });

      if (axios.isAxiosError(error)) {
        const message = error.response?.data?.detail || error.message;
        throw new Error(`Failed to fetch billing info: ${message}`);
      }
      throw error;
    } finally {
      // Clear the promise when done (success or error) so future calls can make new requests
      billingInfoPromise = null;
    }
  })();

  return billingInfoPromise;
}

/**
 * Create a Stripe checkout session for subscription
 * Note: Customer is automatically created/retrieved if needed
 */
export async function createCheckoutSession(
  priceId: string
): Promise<{ sessionId: string; url: string }> {
  try {
    // FastAPI treats price_id as a query parameter for POST requests
    const response = await apiClient.post(
      `/v1/billing/checkout?price_id=${encodeURIComponent(priceId)}`
    );
    return response.data;
  } catch (error) {
    await reportError({
      context: 'Creating checkout session',
      error,
      additionalInfo: { priceId },
    });

    if (axios.isAxiosError(error)) {
      const message = error.response?.data?.detail || error.message;
      throw new Error(`Failed to create checkout session: ${message}`);
    }
    throw error;
  }
}

/**
 * Create a Stripe Billing Portal session for subscription management
 */
export async function createBillingPortalSession(): Promise<{ url: string }> {
  try {
    const response = await apiClient.post('/v1/billing/portal');
    return response.data;
  } catch (error) {
    await reportError({
      context: 'Creating billing portal session',
      error,
    });

    if (axios.isAxiosError(error)) {
      const message = error.response?.data?.detail || error.message;
      throw new Error(`Failed to create billing portal session: ${message}`);
    }
    throw error;
  }
}

/**
 * Initiate self-serve account deletion.
 * Returns 202 immediately; cleanup happens in the background.
 */
export async function requestAccountDeletion(reason?: string): Promise<void> {
  try {
    await apiClient.post('/v1/account/deletion', { reason: reason || '' });
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = error.response?.data?.detail || error.message;
      throw new Error(`Failed to request account deletion: ${message}`);
    }
    throw error;
  }
}

// ============================================================================
// Organizations
// ============================================================================

export type PlanTier = 'free' | 'starter' | 'plus' | 'pro';

export interface OrganizationListItem {
  id: string;
  name: string;
  role: string;
  plan_tier?: PlanTier | string;
}

export interface OrganizationListResponse {
  personal_plan_tier: PlanTier | string;
  organizations: OrganizationListItem[];
}

export interface OrganizationMember {
  user_id: string;
  role: string;
  created_at?: string;
  email?: string | null;
  display_name?: string | null;
  avatar_url?: string | null;
}

export interface OrganizationInvite {
  id: string;
  email: string;
  role: string;
  expires_at: string;
  created_at?: string;
}

export interface OrganizationDetail {
  id: string;
  name: string;
  role: string;
  created_by: string;
  seats_used: number;
  seats_limit: number;
  owner_user_id: string;
  pending_transfer_user_id?: string | null;
  pending_transfer_expires_at?: string | null;
  last_transfer_at?: string | null;
  upgrade_plan?: string | null;
  members?: OrganizationMember[];
  invites?: OrganizationInvite[];
}

export interface AcceptInviteResult {
  organization_id: string;
  name: string;
  role: string;
}

export interface SeatLimitExceededDetail {
  code?: string;
  seats_used?: number;
  seats_limit?: number;
  upgrade_plan?: string;
  message?: string;
}

function formatApiDetail(detail: unknown): string {
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object') {
    const obj = detail as SeatLimitExceededDetail & { message?: string };
    if (obj.message) return obj.message;
    return JSON.stringify(detail);
  }
  return 'Request failed';
}

export async function listOrganizations(): Promise<OrganizationListResponse> {
  try {
    const response = await requestWithRetry(() =>
      apiClient.get('/v1/organizations/')
    );
    const data = response.data;
    // Back-compat if an older API still returns a bare array
    if (Array.isArray(data)) {
      return { personal_plan_tier: 'free', organizations: data };
    }
    return data as OrganizationListResponse;
  } catch (error) {
    await reportError({ context: 'Listing organizations', error });
    if (axios.isAxiosError(error)) {
      throw new Error(
        `Failed to list organizations: ${formatApiDetail(error.response?.data?.detail)}`
      );
    }
    throw error;
  }
}

export async function createOrganization(
  name: string
): Promise<OrganizationDetail> {
  try {
    const response = await apiClient.post('/v1/organizations/', { name });
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const status = error.response?.status;
      const message = formatApiDetail(error.response?.data?.detail);
      // Expected plan gate — don't Slack-report
      if (status !== 403 && status !== 409) {
        await reportError({
          context: 'Creating organization',
          error,
          additionalInfo: { name },
        });
      }
      throw new Error(message || 'Failed to create organization');
    }
    await reportError({
      context: 'Creating organization',
      error,
      additionalInfo: { name },
    });
    throw error;
  }
}

export async function getOrganization(
  organizationId: string
): Promise<OrganizationDetail> {
  try {
    const response = await requestWithRetry(() =>
      apiClient.get(`/v1/organizations/${organizationId}`)
    );
    return response.data;
  } catch (error) {
    await reportError({
      context: 'Fetching organization',
      error,
      additionalInfo: { organizationId },
    });
    if (axios.isAxiosError(error)) {
      throw new Error(
        `Failed to fetch organization: ${formatApiDetail(error.response?.data?.detail)}`
      );
    }
    throw error;
  }
}

export async function updateOrganization(
  organizationId: string,
  name: string
): Promise<OrganizationDetail> {
  try {
    const response = await apiClient.patch(
      `/v1/organizations/${organizationId}`,
      { name }
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(
        `Failed to update organization: ${formatApiDetail(error.response?.data?.detail)}`
      );
    }
    throw error;
  }
}

export async function deleteOrganization(
  organizationId: string
): Promise<void> {
  try {
    await apiClient.delete(`/v1/organizations/${organizationId}`);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(
        `Failed to delete organization: ${formatApiDetail(error.response?.data?.detail)}`
      );
    }
    throw error;
  }
}

export async function createOrganizationInvite(
  organizationId: string,
  email: string
): Promise<OrganizationInvite> {
  try {
    const response = await apiClient.post(
      `/v1/organizations/${organizationId}/invites`,
      { email }
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(
        `Failed to send invite: ${formatApiDetail(error.response?.data?.detail)}`
      );
    }
    throw error;
  }
}

export async function revokeOrganizationInvite(
  organizationId: string,
  inviteId: string
): Promise<void> {
  try {
    await apiClient.delete(
      `/v1/organizations/${organizationId}/invites/${inviteId}`
    );
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(
        `Failed to revoke invite: ${formatApiDetail(error.response?.data?.detail)}`
      );
    }
    throw error;
  }
}

export async function acceptOrganizationInvite(
  token: string
): Promise<AcceptInviteResult> {
  try {
    const response = await apiClient.post(
      `/v1/organizations/invites/${encodeURIComponent(token)}/accept`
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(
        `Failed to accept invite: ${formatApiDetail(error.response?.data?.detail)}`
      );
    }
    throw error;
  }
}

export async function removeOrganizationMember(
  organizationId: string,
  memberUserId: string
): Promise<void> {
  try {
    await apiClient.delete(
      `/v1/organizations/${organizationId}/members/${memberUserId}`
    );
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(
        `Failed to remove member: ${formatApiDetail(error.response?.data?.detail)}`
      );
    }
    throw error;
  }
}

export async function leaveOrganization(organizationId: string): Promise<void> {
  try {
    await apiClient.post(`/v1/organizations/${organizationId}/leave`);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(
        `Failed to leave organization: ${formatApiDetail(error.response?.data?.detail)}`
      );
    }
    throw error;
  }
}

export async function startOrganizationTransfer(
  organizationId: string,
  userId: string
): Promise<OrganizationDetail> {
  try {
    const response = await apiClient.post(
      `/v1/organizations/${organizationId}/transfer`,
      { user_id: userId }
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(
        `Failed to start ownership transfer: ${formatApiDetail(error.response?.data?.detail)}`
      );
    }
    throw error;
  }
}

export async function cancelOrganizationTransfer(
  organizationId: string
): Promise<OrganizationDetail> {
  try {
    const response = await apiClient.post(
      `/v1/organizations/${organizationId}/transfer/cancel`
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(
        `Failed to cancel transfer: ${formatApiDetail(error.response?.data?.detail)}`
      );
    }
    throw error;
  }
}

export async function acceptOrganizationTransfer(
  organizationId: string
): Promise<OrganizationDetail> {
  try {
    const response = await apiClient.post(
      `/v1/organizations/${organizationId}/transfer/accept`
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(
        `Failed to accept transfer: ${formatApiDetail(error.response?.data?.detail)}`
      );
    }
    throw error;
  }
}

export async function moveTemplateToOrganization(
  templateId: string,
  organizationId: string
): Promise<void> {
  try {
    await apiClient.post(`/v1/template/${templateId}/move-to-organization`, {
      organization_id: organizationId,
    });
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(
        `Failed to move template: ${formatApiDetail(error.response?.data?.detail)}`
      );
    }
    throw error;
  }
}
